import { useEffect, useMemo, useState } from 'react';

/**
 * Pagination + multi-select for admin list tables (Services, Packages). Caller does the filtering and
 * passes the already-filtered rows; this owns the page index and the selection Set. Selection is keyed
 * by id and lives independently of the current filter/page, so ticking rows across different pages (or
 * before/after changing a filter) PERSISTS — exactly what bulk actions need. Reset `page` to 0 in the
 * caller whenever a filter changes so you're never stranded on an empty page.
 */
export function useListControls<T extends { id: string }>(filtered: T[], pageSize = 25) {
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (page > totalPages - 1) setPage(0);
  }, [totalPages, page]);

  const pageItems = useMemo(
    () => filtered.slice(page * pageSize, page * pageSize + pageSize),
    [filtered, page, pageSize],
  );

  const isSelected = (id: string): boolean => selected.has(id);

  const toggle = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const setMany = (ids: string[], on: boolean): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  const clear = (): void => setSelected(new Set());

  return { page, setPage, totalPages, pageItems, selected, isSelected, toggle, setMany, clear, pageSize };
}
